import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import Navbar from '../components/Navbar';
import SearchBar from '../components/SearchBar';
import PlatformResults from '../components/PlatformResults';
import Login from '../components/Login';
import OutfitCard from '../components/OutfitCard';
import {
  parseSearchQuery,
  searchProductsFromBackend,
} from '../services/claude';
import { auth } from '../services/firebase';
import { saveSearchHistory } from '../services/userService';

const OUTFIT_KEYWORDS = [
  'outfit', 'what to wear', 'what should i wear',
  'farewell', 'wedding', 'party', 'date night', 'first date',
  'interview', 'office look', 'night out', 'birthday',
  'beach', 'casual outing', 'puja', 'festival', 'festive',
  'what do i wear', 'help me dress', 'dress me', 'look for'
];

function Home() {
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState(null);
  const [bodyProfile, setBodyProfile] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [searchLimitReached, setSearchLimitReached] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [searchStatus, setSearchStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  // Outfit state
  const [outfit, setOutfit] = useState(null);
  const [isOutfitLoading, setIsOutfitLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setSearchLimitReached(false);
        setLimitInfo(null);
        fetchSearchStatus(currentUser.uid);
      } else {
        setSearchStatus(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchSearchStatus = async (userId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${BACKEND_URL}/api/search-status?userId=${userId}`);
      const data = await response.json();
      setSearchStatus(data);
    } catch (error) {
      console.error('Status fetch error:', error);
    }
  };

  // Detect if query is an outfit request
  const isOutfitRequest = (query) => {
    const q = query.toLowerCase();
    return OUTFIT_KEYWORDS.some(k => q.includes(k));
  };

  const handleSearch = async (query) => {
    // Clear previous results
    setSearchResults(null);
    setOutfit(null);
    setSearchLimitReached(false);
    setLimitInfo(null);
    setSearchQuery(query);

    // ── OUTFIT REQUEST ──
    if (isOutfitRequest(query)) {
      setIsOutfitLoading(true);
      setIsLoading(false);
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(`${BACKEND_URL}/api/outfit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: query,
            userProfile: {
              gender: bodyProfile?.gender,
              topSize: bodyProfile?.topSize || bodyProfile?.size,
              bottomSize: bodyProfile?.bottomSize,
            }
          })
        });
        const data = await response.json();
        if (data.success) {
          setOutfit(data.outfit);
        }
      } catch (error) {
        console.error('Outfit error:', error);
      } finally {
        setIsOutfitLoading(false);
      }
      return; // Don't run normal search
    }

    // ── NORMAL SEARCH ──
    setIsLoading(true);
    console.log('Searching for:', query);

    try {
      const result = await parseSearchQuery(
        query,
        bodyProfile?.shape,
        bodyProfile?.gender,
        bodyProfile?.topSize || bodyProfile?.size,
        bodyProfile?.bottomSize
      );

      console.log('Claude response:', result);

      if (!result) {
        setIsLoading(false);
        return;
      }

      const productData = await searchProductsFromBackend(
        result,
        user?.uid || 'guest'
      );

      console.log('Product data:', productData);

      if (productData?.type === 'limit_reached') {
        setSearchLimitReached(true);
        setLimitInfo(productData);
        setShowLogin(!user);
        setIsLoading(false);
        return;
      }

      setSearchStatus({
        apifyRemaining: productData.apifyRemaining,
        redirectRemaining: productData.redirectRemaining,
        resetAfterHours: productData.resetAfterHours,
      });

      setSearchResults({
        ...result,
        productData,
        searchId: Date.now(),
      });

      if (user) {
        await saveSearchHistory(user.uid, query);
      }

    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusMessage = () => {
    if (!searchStatus) return null;
    const { apifyRemaining, redirectRemaining, resetAfterHours } = searchStatus;
    if (apifyRemaining > 0) {
      return `✦ ${apifyRemaining} AI searches + ${redirectRemaining} standard searches remaining · Resets in ${resetAfterHours}h`;
    }
    if (redirectRemaining > 0) {
      return `✦ ${redirectRemaining} searches remaining today · Resets in ${resetAfterHours}h`;
    }
    return `✦ Daily searches exhausted · Resets in ${resetAfterHours}h`;
  };

  const getLimitMessage = () => {
    if (!limitInfo) return null;
    const { isGuest, resetAfterHours } = limitInfo;
    if (isGuest) {
      return {
        icon: '🔒',
        title: 'Sign in to see more results',
        text: `You have used your ${limitInfo.limit} free searches. Sign in to unlock more daily searches.`,
        showLoginBtn: true,
      };
    }
    return {
      icon: '⏳',
      title: 'Daily searches exhausted',
      text: `You have used all searches for today. Resets every ${resetAfterHours} hours.`,
      showLoginBtn: false,
    };
  };

  const limitMessage = getLimitMessage();

  return (
    <div style={styles.page}>
      <Navbar
        onUploadClick={() => {}}
        onBodyProfileLoad={(bp) => setBodyProfile(bp)}
      />

      {/* Personalisation Banner */}
      {bodyProfile && (
        <div style={styles.personalisedBar}>
          ✦ Personalised for <strong>{bodyProfile.shapeName}</strong> body
          {' '}· Top <strong>{bodyProfile.topSize || bodyProfile.size}</strong>
          {bodyProfile.bottomSize && (
            <> · Bottom <strong>{bodyProfile.bottomSize}</strong></>
          )}
        </div>
      )}

      {/* Search Status Bar */}
      {user && searchStatus && (
        <div style={{
          ...styles.searchStatusBar,
          background: searchStatus.redirectRemaining === 0 ? '#FDECEA' : '#E8F5EE',
          borderBottomColor: searchStatus.redirectRemaining === 0 ? '#F5C6C2' : '#C3E6CB',
          color: searchStatus.redirectRemaining === 0 ? '#D93025' : '#2E8B57',
        }}>
          {getStatusMessage()}
        </div>
      )}

      {/* Hero Section */}
      <div style={{
        ...styles.hero,
        padding: isMobile ? '40px 20px 30px' : '60px 40px 40px',
      }}>
        <div style={styles.badge}>✦ AI-Powered Fashion Discovery</div>

        <h1 style={{
          ...styles.heroTitle,
          fontSize: isMobile ? '34px' : '52px',
          marginBottom: isMobile ? '10px' : '12px',
        }}>
          Find fashion that <em style={styles.em}>fits</em> your body & budget
        </h1>

        <p style={{
          ...styles.heroSub,
          fontSize: isMobile ? '14px' : '15px',
        }}>
          Search naturally — or ask for a complete outfit for any occasion!
        </p>

        <SearchBar onSearch={handleSearch} />

        {!user && (
          <p style={styles.guestHint}>
            🔍 2 free searches ·{' '}
            <span style={styles.loginLink} onClick={() => setShowLogin(true)}>
              Sign in
            </span>{' '}
            for more results
          </p>
        )}
      </div>

      {/* ── OUTFIT LOADING ── */}
      {isOutfitLoading && (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '40px 20px' : '60px 40px',
          flex: 1,
        }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>✨</div>
          <p style={{
            fontSize: '16px',
            color: '#8A7F7A',
            fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic',
          }}>
            Building your perfect outfit...
          </p>
        </div>
      )}

      {/* ── OUTFIT RESULT ── */}
      {outfit && !isOutfitLoading && (
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: isMobile ? '16px' : '24px 40px',
          width: '100%',
        }}>
          <OutfitCard
            outfit={outfit}
            onClose={() => setOutfit(null)}
          />

          {/* Also search normally option */}
          <div style={{
            textAlign: 'center',
            marginTop: '16px',
            padding: '12px',
            background: '#fff9f7',
            borderRadius: '12px',
            border: '1px solid #ffe4d6',
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#8A7F7A' }}>
              Want to search a specific item instead?
            </p>
            <button
              onClick={() => {
                setOutfit(null);
                handleSearch(searchQuery.replace(/outfit|for my|for a/gi, '').trim());
              }}
              style={{
                background: 'none',
                border: '1px solid #C8715A',
                color: '#C8715A',
                borderRadius: '20px',
                padding: '6px 16px',
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Search "{searchQuery}" across platforms →
            </button>
          </div>
        </div>
      )}

      {/* ── NORMAL SEARCH LOADING ── */}
      {isLoading && !isOutfitLoading && (
        <div style={{
          ...styles.loading,
          padding: isMobile ? '40px 20px' : '60px 40px',
        }}>
          <div style={styles.loadingDots}>✦</div>
          <p style={styles.loadingText}>
            Finding the best matches for you...
          </p>
        </div>
      )}

      {/* ── LIMIT REACHED ── */}
      {!isLoading && !isOutfitLoading && searchLimitReached && limitMessage && (
        <div style={{
          ...styles.limitReached,
          padding: isMobile ? '40px 20px' : '60px 40px',
        }}>
          <div style={styles.limitIcon}>{limitMessage.icon}</div>
          <h3 style={styles.limitTitle}>{limitMessage.title}</h3>
          <p style={styles.limitText}>{limitMessage.text}</p>
          {limitMessage.showLoginBtn && (
            <button
              style={styles.loginPromptBtn}
              onClick={() => setShowLogin(true)}
            >
              Sign In for More Results
            </button>
          )}
          {!limitMessage.showLoginBtn && (
            <div style={styles.resetInfo}>
              <div style={styles.resetIcon}>🕐</div>
              <p style={styles.resetText}>
                Resets every <strong>{limitInfo?.resetAfterHours || 24} hours</strong>
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── NORMAL SEARCH RESULTS ── */}
      {!isLoading && !isOutfitLoading && !outfit && searchResults && (
        <PlatformResults
          key={searchResults.searchId}
          searchResults={searchResults}
          searchQuery={searchQuery}
          onNewSearch={handleSearch}
          userBodyShape={bodyProfile?.shape}
          userBodyShapeName={bodyProfile?.shapeName}
          isMobile={isMobile}
        />
      )}

      {/* ── EMPTY STATE ── */}
      {!isLoading && !isOutfitLoading && !outfit && !searchResults && !searchLimitReached && (
        <div style={{
          ...styles.emptyState,
          padding: isMobile ? '40px 20px' : '60px 40px',
        }}>
          <div style={styles.emptyIcon}>🔍</div>
          <h3 style={styles.emptyTitle}>What are you looking for today?</h3>
          <p style={styles.emptyText}>
            Search for any clothing — or ask for a complete outfit for any occasion!
          </p>

          <div style={{
            ...styles.examplesGrid,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          }}>
            {[
              { emoji: '✨', text: 'Outfit for my college farewell, girl, budget 4000' },
              { emoji: '🥻', text: 'What to wear for a wedding as a guest' },
              { emoji: '👔', text: 'Office outfit for men under 3000' },
              { emoji: '🎉', text: 'Birthday party outfit for girl' },
              { emoji: '👗', text: 'Casual saree for office' },
              { emoji: '👖', text: 'Baggy jeans under 1,500' },
            ].map((example, i) => (
              <button
                key={i}
                style={styles.exampleBtn}
                onClick={() => handleSearch(example.text)}
              >
                <span style={styles.exampleEmoji}>{example.emoji}</span>
                <span style={styles.exampleText}>{example.text}</span>
              </button>
            ))}
          </div>

          <div style={{
            ...styles.featuresRow,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '10px' : '24px',
          }}>
            {[
              { icon: '🎯', text: 'Body shape personalisation' },
              { icon: '✨', text: 'Complete outfit builder' },
              { icon: '🛍️', text: 'Multiple platforms' },
              { icon: '💰', text: 'Budget filtering' },
            ].map((f, i) => (
              <div key={i} style={styles.featureItem}>
                <span style={styles.featureIcon}>{f.icon}</span>
                <span style={styles.featureText}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Page Footer */}
      <div style={styles.pageFooter}>
        <a href="/privacy" style={styles.privacyLink}>Privacy Policy</a>
        <span style={styles.footerDot}>·</span>
        <span style={styles.footerCopy}>© 2026 Fitthea</span>
      </div>

      {showLogin && <Login onClose={() => setShowLogin(false)} />}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#FFF9F4',
  },
  personalisedBar: {
    background: '#F5EDD8',
    borderBottom: '1px solid #C9A96E',
    padding: '8px 20px',
    fontSize: '12px',
    color: '#9B4D3A',
    textAlign: 'center',
    lineHeight: '1.6',
  },
  searchStatusBar: {
    padding: '6px 20px',
    fontSize: '11px',
    textAlign: 'center',
    fontWeight: '500',
    borderBottom: '1px solid',
    transition: 'all 0.3s',
    lineHeight: '1.6',
  },
  hero: {
    textAlign: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: '#F5EDD8',
    color: '#9B4D3A',
    border: '1px solid #C9A96E',
    borderRadius: '100px',
    padding: '6px 16px',
    fontSize: '11px',
    fontWeight: '500',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '24px',
  },
  heroTitle: {
    fontFamily: "'Playfair Display', serif",
    fontWeight: '700',
    lineHeight: '1.15',
    color: '#2C2420',
  },
  em: {
    fontStyle: 'italic',
    color: '#C8715A',
  },
  heroSub: {
    color: '#8A7F7A',
    fontWeight: '300',
    marginBottom: '36px',
    lineHeight: '1.6',
  },
  guestHint: {
    marginTop: '16px',
    fontSize: '12px',
    color: '#8A7F7A',
  },
  loginLink: {
    color: '#C8715A',
    cursor: 'pointer',
    fontWeight: '600',
    textDecoration: 'underline',
  },
  loading: {
    textAlign: 'center',
    flex: 1,
  },
  loadingDots: {
    fontSize: '32px',
    marginBottom: '16px',
    color: '#C8715A',
  },
  loadingText: {
    fontSize: '16px',
    color: '#8A7F7A',
    fontFamily: "'Playfair Display', serif",
    fontStyle: 'italic',
  },
  limitReached: {
    textAlign: 'center',
    maxWidth: '500px',
    margin: '0 auto',
    flex: 1,
  },
  limitIcon: {
    fontSize: '56px',
    marginBottom: '20px',
  },
  limitTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '22px',
    fontWeight: '700',
    color: '#2C2420',
    marginBottom: '12px',
  },
  limitText: {
    fontSize: '14px',
    color: '#8A7F7A',
    lineHeight: '1.8',
    marginBottom: '28px',
  },
  loginPromptBtn: {
    background: '#2C2420',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 32px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    display: 'inline-block',
  },
  resetInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    background: '#F5F5F5',
    borderRadius: '12px',
    padding: '16px 24px',
    marginTop: '16px',
    maxWidth: '300px',
    margin: '16px auto 0',
  },
  resetIcon: { fontSize: '24px' },
  resetText: {
    fontSize: '13px',
    color: '#8A7F7A',
    margin: 0,
  },
  emptyState: {
    textAlign: 'center',
    maxWidth: '640px',
    margin: '0 auto',
    flex: 1,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '22px',
    fontWeight: '700',
    color: '#2C2420',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#8A7F7A',
    lineHeight: '1.7',
    marginBottom: '32px',
  },
  examplesGrid: {
    display: 'grid',
    gap: '12px',
    maxWidth: '500px',
    margin: '0 auto 40px',
  },
  exampleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: 'white',
    border: '1.5px solid #EDE4DA',
    borderRadius: '12px',
    padding: '14px 16px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'DM Sans', sans-serif",
    width: '100%',
    transition: 'all 0.2s',
  },
  exampleEmoji: {
    fontSize: '20px',
    flexShrink: 0,
  },
  exampleText: {
    fontSize: '13px',
    color: '#2C2420',
    fontWeight: '500',
    lineHeight: '1.4',
  },
  featuresRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: '40px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  featureIcon: { fontSize: '16px' },
  featureText: {
    fontSize: '12px',
    color: '#8A7F7A',
    fontWeight: '500',
  },
  pageFooter: {
    textAlign: 'center',
    padding: '24px 20px',
    borderTop: '1px solid #EDE4DA',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: 'auto',
  },
  privacyLink: {
    fontSize: '12px',
    color: '#C8715A',
    textDecoration: 'none',
  },
  footerDot: {
    fontSize: '12px',
    color: '#8A7F7A',
  },
  footerCopy: {
    fontSize: '12px',
    color: '#8A7F7A',
  },
};

export default Home;